/**
 * Image upload utilities for handling profile pictures and file uploads
 * Following AGENT.md: Complete functionality, proper error handling, no placeholders
 */

export interface UploadImageOptions {
  uploadUrl: string;
  canvas: HTMLCanvasElement;
  contentType?: string;
}

/**
 * Type guard to validate upload URLs
 */
export const isValidUploadUrl = (
  url: string | null | undefined
): url is string => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObject = new URL(url);
    return !!urlObject;
  } catch {
    return false;
  }
};

/**
 * Converts base64 image data to File object
 * @param base64Image Base64 encoded image string
 * @param contentType MIME type of the image
 * @returns File object ready for upload
 * @throws Error if base64 data is invalid
 */
export const base64ImageToFile = (
  base64Image: string,
  contentType: string = 'image/png'
): File => {
  const fileString =
    typeof base64Image === 'string' ? base64Image.split(',')[1] : '';

  if (!fileString) {
    throw new Error('Invalid base64 image data');
  }

  // Convert base64 to ArrayBuffer
  const binaryString = atob(fileString);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new File([bytes], 'profile-image.png', { type: contentType });
};

/**
 * Uploads image to S3 using pre-signed URL
 * @param options Upload configuration
 * @throws Error if upload fails or validation fails
 */
export const uploadImage = async (
  options: UploadImageOptions
): Promise<void> => {
  const { uploadUrl, canvas, contentType = 'image/png' } = options;

  if (!isValidUploadUrl(uploadUrl)) {
    throw new Error('Invalid upload URL provided');
  }

  if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
    throw new Error('Valid canvas element is required');
  }

  const base64Data = canvas.toDataURL(contentType);
  const imageFile = base64ImageToFile(base64Data, contentType);

  try {
    await new Promise<void>((resolve, reject) => {
      // XMLHttpRequest so the Content-Type header can be sent empty — the
      // presigned URL is signed without a Content-Type, and S3 rejects a PUT
      // that includes one with a 403 (fetch always sends the Blob's type).
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', '');
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(
            new Error(
              `Upload failed: ${xhr.status} ${xhr.statusText || 'Unknown error'}`
            )
          );
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(imageFile);
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Image upload failed: ${error.message}`);
    }
    throw new Error('Image upload failed with unknown error');
  }
};

/**
 * Specialized utility for uploading user profile images
 * @param uploadUrl Pre-signed S3 upload URL
 * @param canvas Canvas element containing the image
 * @throws Error if parameters are invalid or upload fails
 */
export const uploadUserProfileImage = async (
  uploadUrl: string | null | undefined,
  canvas: HTMLCanvasElement | null
): Promise<void> => {
  if (!canvas) {
    throw new Error('No image canvas provided');
  }

  if (!uploadUrl) {
    throw new Error('No upload URL provided');
  }

  return uploadImage({
    uploadUrl,
    canvas,
    contentType: 'image/png',
  });
};

/**
 * Validates image file size and type
 * @param file File to validate
 * @param maxSizeBytes Maximum file size in bytes
 * @param allowedTypes Array of allowed MIME types
 * @returns true if valid, throws Error if invalid
 */
export const validateImageFile = (
  file: File,
  maxSizeBytes: number = 5 * 1024 * 1024, // 5MB default
  allowedTypes: string[] = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
  ]
): boolean => {
  if (!file) {
    throw new Error('No file provided');
  }

  if (file.size > maxSizeBytes) {
    const maxSizeMB = Math.round(maxSizeBytes / (1024 * 1024));
    throw new Error(`File size exceeds ${maxSizeMB}MB limit`);
  }

  if (!allowedTypes.includes(file.type)) {
    throw new Error(
      `File type ${
        file.type
      } is not allowed. Allowed types: ${allowedTypes.join(', ')}`
    );
  }

  return true;
};

/**
 * Creates a canvas element from an image file
 * @param file Image file
 * @param maxWidth Maximum width for resizing
 * @param maxHeight Maximum height for resizing
 * @returns Promise that resolves to canvas element
 */
export const createCanvasFromImageFile = async (
  file: File,
  maxWidth: number = 400,
  maxHeight: number = 400
): Promise<HTMLCanvasElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};
